import { Injectable, computed, effect, signal } from '@angular/core';

export type Theme = 'dark' | 'light';

const STORAGE_KEY = 'ksh-theme';

/** Kept in step with `--c-bg` in each palette so the mobile URL bar matches. */
const CHROME_COLOR: Record<Theme, string> = { dark: '#08080C', light: '#FBF8EF' };

/**
 * THEME SERVICE
 * -------------
 * One signal repaints the entire interface. Every colour in the app resolves
 * from a custom property, and the light palette redefines those properties
 * under `html[data-theme='light']` — so no component needs to know a theme
 * exists, and nothing can be "missed" by forgetting to handle a second case.
 *
 * First visit follows the OS preference; after that the reader's own choice
 * wins and is remembered. `index.html` applies the stored value before Angular
 * boots, so the page never flashes the wrong palette on load.
 */
@Injectable({ providedIn: 'root' })
export class ThemeService {
  readonly theme = signal<Theme>(readInitial());
  readonly isLight = computed(() => this.theme() === 'light');

  /** The hero/nav lockup is dark artwork; on a pale ground it needs the blue cut. */
  readonly logoSrc = computed(() => (this.isLight() ? 'blue_logo.png' : 'logo.png'));

  constructor() {
    effect(() => this.apply(this.theme()));
  }

  toggle(): void {
    const next: Theme = this.theme() === 'dark' ? 'light' : 'dark';

    // A palette swap is the one moment the whole viewport changes at once, so
    // it cross-fades as a single image rather than as ~40 properties each on
    // their own timing. Per-element transitions can't do this: they are
    // suppressed during the swap (see `apply`) precisely because staggering
    // them looks like a smear.
    const doc = document as Document & {
      startViewTransition?: (cb: () => void) => unknown;
    };
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (!doc.startViewTransition || reduced) {
      this.theme.set(next);   // Firefox, and anyone who asked for less motion
      return;
    }

    doc.startViewTransition(() => {
      // The callback has to mutate the DOM before it returns — an Angular
      // effect would land after the API has already captured the "after"
      // frame — so the attribute is written here and the effect, which runs
      // later with the same value, is a no-op.
      this.theme.set(next);
      this.apply(next);
    });
  }

  private apply(theme: Theme): void {
    const root = document.documentElement;

    // Suspend transitions across the swap. Two reasons, and the second is not
    // cosmetic: letting ~40 rules cross-fade on their own schedules looks like
    // a smear, and Chrome leaves an element whose `color` is a `var()` behind a
    // `transition` stuck on the *previous* palette when that var changes —
    // the footer links kept their light-mode grey on a black page.
    root.classList.add('theme-switching');
    root.setAttribute('data-theme', theme);
    // Force the new values to be computed while transitions are still off; by
    // the time the class comes back off there is nothing left to animate.
    void root.offsetHeight;
    // A timeout rather than requestAnimationFrame: rAF never fires in a
    // background tab, which would strand the app with every transition
    // disabled for the life of the page.
    setTimeout(() => root.classList.remove('theme-switching'), 0);
    // Browser chrome — form controls, scrollbars, the mobile URL bar — is not
    // ours to paint, so hand it the same answer we gave the stylesheet.
    root.style.colorScheme = theme;
    document.querySelector('meta[name="theme-color"]')
      ?.setAttribute('content', CHROME_COLOR[theme]);

    try {
      localStorage.setItem(STORAGE_KEY, theme);
    } catch {
      // Private browsing: the choice still applies, it just won't outlive the tab.
    }
  }
}

function readInitial(): Theme {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved === 'light' || saved === 'dark') return saved;
  } catch {
    // Storage blocked — fall through to the OS preference.
  }
  return window.matchMedia('(prefers-color-scheme: light)').matches ? 'light' : 'dark';
}
